import { useEffect, useState } from 'react';
import ContentWrapper from '../views/components/ContentWrapper';
import { Form, Button } from 'react-bootstrap';
import { getBotSettings, saveNewFunFacts, updateBotSettings, getFunFacts, updateFunFact, deleteFunFact } from '../data/dataApi';
import ConfirmationModal from '../views/components/ConfirmationModal';
import { useUserState } from "../views/components/Mode";
import { isValidUrl } from '../views/helpers';
import EditBtn from "../images/edit.svg";
import DeleteBtn from "../images/delete.svg";

const BotSettings = () => {
    const { userCapabilities } = useUserState();

    const [supportMessageThreshold, setSupportMessageThreshold] = useState(0);
    const [supportMessageThresholdError, setSupportMessageThresholdError] = useState();
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const initialNewFunFacts = [{ fact: "", url: "" }];
    const [newFunFacts, setNewFunFacts] = useState(initialNewFunFacts);
    const [funFactsError, setFunFactsError] = useState();
    const [savedFunFacts, setSavedFunFacts] = useState([]);
    const initialEditingModeItem = {
        fact: "",
        url: "",
    };
    const [editingModeItem, setEditingModeItem] = useState(initialEditingModeItem);
    const [savingFunFact, setSavingFunFact] = useState(false);
    const [deleteFunFactId, setDeleteFunFactId] = useState();
    const [showDeleteFunFactModal, setShowDeleteFunFactModal] = useState(false);
    const [showEditSuccessModal, setShowEditSuccessModal] = useState(false);
    const [helpRequestEmails, setHelpRequestEmails] = useState([""]);
    const [savedHelpRequestEmails, setSavedHelpRequestEmails] = useState([]);
    const [deleteHelpRequestEmail, setDeleteHelpRequestEmail] = useState("");
    const [helpRequestEmailsError, setHelpRequestEmailsError] = useState(false);
    const [showDeleteHelpRequestEmailModal, setShowDeleteHelpRequestEmailModal] = useState(false);
    const [editFunFactError, setEditFunFactError] = useState(false);
    const [error, setError] = useState(false);

    const settings = {
        supportMessageThreshold,
        helpRequestEmails: [...helpRequestEmails.filter(email => email.length), ...savedHelpRequestEmails],
    };

    const handleSaveBotSettings = async (disableConfirmation = false, newSettings = null) => {
        const response = await updateBotSettings(newSettings || settings);

        if(response.success) {
            !disableConfirmation && setShowConfirmationModal(true);

            setHelpRequestEmails([""]);
            setSavedHelpRequestEmails(prevState => [...helpRequestEmails.filter(email => email.length), ...prevState.filter(email => email.length)]);
        } else {
            setError(response.error.message);
        }
    };

    const updateFunFacts = (index, type, val) => {
        const newFunFactsList = [...newFunFacts];
        newFunFactsList[index][type] = val;
        setNewFunFacts(newFunFactsList);
    };

    const handleFunFactsSubmit = async () => {
        setFunFactsError("");

        for(let i = 0; i < newFunFacts.length; i++) {
            const funFact = newFunFacts[i];
            if(!funFact.fact) {
                setFunFactsError("Please fill in all the facts.");
                return;
            } else if(!isValidUrl(funFact.url)) {
                setFunFactsError("Please enter valid URL(s).");
                return;
            }
        }

        const response = await saveNewFunFacts(newFunFacts);

        if(response.success) {
            const newlySavedFunFacts = response.data.map(item => item.rows[0]);

            setSavedFunFacts(prevState => [...newlySavedFunFacts, ...prevState]);
            setNewFunFacts(initialNewFunFacts);
        } else {
            setFunFactsError(response.error.message);
        }
    };

    const handleSaveFunFact = async () => {
        setEditFunFactError("");

        if(!editingModeItem.fact) {
            setFunFactsError("Please fill in the fact.");
            return;
        } else if(!isValidUrl(editingModeItem.url)) {
            setFunFactsError("Please enter a valid URL.");
            return;
        } else if(!editFunFactError) {
            setSavingFunFact(true);
            const response = await updateFunFact(editingModeItem);
            
            if(response.success) {
                setSavedFunFacts(prevState => {
                    const newSavedFunFacts = [...prevState].map(item => {
                        if(item.id === editingModeItem.id) {
                            return editingModeItem;
                        } else {
                            return item;
                        }
                    });

                    return newSavedFunFacts;
                });
                setEditingModeItem(initialEditingModeItem);
                setShowEditSuccessModal(true);
            } else {
                setEditFunFactError(response.error.message);
            }

            setSavingFunFact(false);
        }
    };

    const handleDeleteClick = id => {
        setDeleteFunFactId(id);
        setShowDeleteFunFactModal(true);
    };

    const handleFunFactDelete = async () => {
        setError("");

        const response = await deleteFunFact(deleteFunFactId);

        if (response.success) {
            setSavedFunFacts(prevState => prevState.filter(item => item.id !== deleteFunFactId));
            setEditingModeItem(initialEditingModeItem);
        } else {
            setError(response.error.message);
        }

        return response.success;
    };

    const handleDeleteHelpRequestEmailClick = email => {
        setDeleteHelpRequestEmail(email);
        setShowDeleteHelpRequestEmailModal(true);
    };

    const handleDeleteHelpRequestEmail = async () => {
        setHelpRequestEmailsError("");

        const newHelpRequestEmails = savedHelpRequestEmails.filter(email => email !== deleteHelpRequestEmail);

        setSavedHelpRequestEmails(newHelpRequestEmails);

        const newSettings = {
            ...settings,
            helpRequestEmails: newHelpRequestEmails,
        };

        await handleSaveBotSettings(true, newSettings);

        return true;
    };

    useEffect(() => {
        (async () => {
            const botSettingsResponse = await getBotSettings();
            const funFactsResponse = await getFunFacts();

            const settings = botSettingsResponse.data.rows;
            const currentFunFacts = funFactsResponse.data.rows;

            for(const setting of settings) {
                if(setting.type === "supportMessageThreshold") {
                    setSupportMessageThreshold(setting.value);
                } else if(setting.type === "helpRequestEmails") {
                    setSavedHelpRequestEmails(JSON.parse(setting.value).filter(email => email.length));
                }
            }

            setSavedFunFacts(currentFunFacts);
        })();
    }, []);

    return (
        <ContentWrapper className="scrollable">
            <div id="bot-settings" className="wrapper">
                <h4 className="tif-pl-25 tif-mb-20">Bot Support Message Threshold</h4>
                <Form className="secondary-form">
                    <div className="card overflow-hidden bg-transparent">
                        <Form.Group className="form-group secondary-form tif-bg-color-india tif-py-20 tif-pr-70 tif-pl-25" controlId="formSupportMessageThreshold">
                            <Form.Label className="tif-mb-15">Used to determine the number of questions to be asked before triggering a support message</Form.Label>
                            <Form.Control type="number" className="small" value={ supportMessageThreshold } onChange={e => setSupportMessageThreshold(e.target.value)} placeholder="Type here" />
                            { supportMessageThresholdError && <p className="error-text">{ supportMessageThresholdError }</p> }
                        </Form.Group>
                    </div>
                    <Button className="quarternary-btn tif-mt-md-55 tif-mt-30" onClick={() => handleSaveBotSettings()} disabled={ !userCapabilities.includes("update_bot_settings") }>Save</Button>
                </Form>

                <h4 className="tif-pl-25 tif-mb-20 tif-mt-65">Fun Facts</h4>
                <Form className="secondary-form tif-mb-120">
                    <div className="card overflow-hidden bg-transparent">
                        {
                            newFunFacts.map((fact, index) =>
                                <div className={`tif-pt-20 tif-pr-70 tif-pl-25 tif-pb-35 ${ index % 2 === 0 ? 'tif-bg-color-india' : 'tif-bg-color-bravo' }`} key={ index }>
                                    <Form.Group className="form-group secondary-form tif-mb-45" controlId={`formFact_${ index }`}>
                                        <Form.Label className="tif-font-md tif-mb-15">Fact</Form.Label>
                                        <Form.Control type="text" as="textarea" className="small" value={ fact.fact } onChange={e => updateFunFacts(index, "fact", e.target.value)} placeholder="Type here" />
                                        {
                                            fact.fact ?
                                                <Form.Text>
                                                    Did you know that { fact.fact }?
                                                </Form.Text>
                                            :
                                                <></>
                                        }
                                    </Form.Group>
                                    <Form.Group className="form-group secondary-form" controlId={`formFactUrl_${ index }`}>
                                        <Form.Label className="tif-font-md tif-mb-15">Source URL</Form.Label>
                                        <Form.Control type="url" className="small" value={ fact.url } onChange={e => updateFunFacts(index, "url", e.target.value)} placeholder="Type here" />
                                    </Form.Group>
                                </div>
                            )
                        }
                    </div>
                    <Button className="senary-btn tif-mt-15 d-block tif-ml-25 tif-mb-25" onClick={() => setNewFunFacts(prevState => [...prevState, { fact: "", url: "" }])}>+ Add Fact</Button>

                    { funFactsError && <p className="error-text">{ funFactsError }</p> }

                    <Button className="quarternary-btn" onClick={handleFunFactsSubmit} disabled={ !userCapabilities.includes("update_bot_settings") }>Save</Button>
                </Form>

                {
                    savedFunFacts.map((item, index) => (
                        <div className="card tif-pt-20 tif-pb-30 tif-pl-30 tif-pr-85 tif-bg-light-color-papa tif-mb-30 tif-color-white tif-light-color-november tif-bg-color-bravo" key={index}>
                            {
                                editingModeItem.id !== item.id ?
                                    <p className="message">{item.fact}</p>
                                :
                                    <Form className="secondary-form">
                                        <Form.Group className="form-group tif-mb-20" controlId={`formEditFact_${index}`}>
                                            <Form.Label className="tif-mb-15">Fact</Form.Label>
                                            <Form.Control
                                                placeholder="Type here"
                                                as="textarea"
                                                className="small tif-mb-15"
                                                value={editingModeItem.fact}
                                                onChange={e => setEditingModeItem({ ...editingModeItem, fact: e.target.value })}
                                            />
                                            {
                                                editingModeItem.fact ?
                                                    <Form.Text>
                                                        Did you know that { editingModeItem.fact }?
                                                    </Form.Text>
                                                :
                                                    <></>
                                            }
                                        </Form.Group>

                                        <Form.Group className="form-group" controlId={`formEditUrl_${index}`}>
                                            <Form.Label className="tif-mb-15">URL</Form.Label>
                                            <Form.Control
                                                placeholder="Type here"
                                                value={editingModeItem.url}
                                                onChange={e => setEditingModeItem({ ...editingModeItem, url: e.target.value })}
                                                className="small"
                                            />
                                        </Form.Group>

                                        <Button className="quarternary-btn tif-mt-35" onClick={ handleSaveFunFact } disabled={ savingFunFact }>{ savingFunFact ? "Saving..." : "Save" }</Button>
                                        <Button className="septenary-btn tif-mt-35 tif-ml-20" onClick={() => setEditingModeItem(initialEditingModeItem)} disabled={ savingFunFact }>Cancel</Button>
                                        
                                        { editFunFactError && <p className="error-text">{ editFunFactError }</p> }
                                    </Form>
                            }
                            
                            <div className="edit-bot">
                                {
                                    userCapabilities.includes("update_bot_settings") &&
                                    <>
                                        <Button className="edit-btn tif-mb-15" onClick={() => setEditingModeItem(item)} disabled={ editingModeItem.id === item.id }>
                                            <img src={EditBtn} alt="Edit icon" />
                                        </Button>
                                        <Button className="edit-btn" onClick={() => handleDeleteClick(item.id)}>
                                            <img src={DeleteBtn} alt="Delete icon" />
                                        </Button>
                                    </>
                                }
                            </div>
                        </div>
                    ))
                }

                {
                    userCapabilities.includes("update_bot_settings") &&
                        <>
                            <h4 className="tif-pl-25 tif-mb-20">Help Requests Email Notifications</h4>
                            <Form className="secondary-form tif-mb-120">
                                <div className="card overflow-hidden bg-transparent">
                                    {
                                        helpRequestEmails.map((email, index) =>
                                            <Form.Group className="form-group secondary-form tif-bg-color-india tif-py-20 tif-pr-70 tif-pl-25" controlId={`formHelpRequestsEmailNotifications_${index}`} key={index}>
                                                <Form.Label className="tif-mb-15">Email</Form.Label>
                                                <Form.Control type="email" className="small" value={ email } onChange={e => setHelpRequestEmails(prevState => prevState.map((item, i) => i === index ? e.target.value : item))} placeholder="Type here" />
                                                { helpRequestEmailsError && <p className="error-text">{ helpRequestEmailsError }</p> }
                                            </Form.Group>
                                        )
                                    }
                                </div>
                                <Button className="senary-btn tif-mt-15 d-block tif-ml-25 tif-mb-25" onClick={() => setHelpRequestEmails(prevState => [...prevState, ""])}>+ Add Email</Button>
                                <Button className="quarternary-btn tif-mt-md-55 tif-mt-30" onClick={() => handleSaveBotSettings()} disabled={ !userCapabilities.includes("update_bot_settings") }>Save</Button>
                            </Form>
                        </>
                }

                {
                    savedHelpRequestEmails.length ?
                        savedHelpRequestEmails.map((item, index) => (
                            <div className="card tif-pt-20 tif-pb-30 tif-pl-30 tif-pr-85 tif-bg-light-color-papa tif-mb-30 tif-color-white tif-light-color-november tif-bg-color-bravo" key={index}>
                                <p className="message">{item}</p>
                                
                                <div className="edit-bot">
                                    {
                                        userCapabilities.includes("update_bot_settings") &&
                                            <Button className="edit-btn" onClick={() => handleDeleteHelpRequestEmailClick(item)}>
                                                <img src={DeleteBtn} alt="Delete icon" />
                                            </Button>
                                    }
                                </div>
                            </div>
                        ))
                    :
                        <></>
                }
            </div>
            {
                userCapabilities.includes("update_bot_settings") &&
                    <>
                        <ConfirmationModal
                            show={showConfirmationModal}
                            changeVisibility={setShowConfirmationModal}
                            type="success"
                            successMessage="You have successfully saved the bot settings."
                            close={
                                <Button className="tertiary-btn" onClick={() => setShowConfirmationModal(false)}>
                                    Back to Bot Settings
                                </Button>
                            }
                        />
                        <ConfirmationModal
                            show={showDeleteFunFactModal}
                            changeVisibility={setShowDeleteFunFactModal}
                            type="delete"
                            confirmationButton="Delete Fun Fact"
                            confirmationMessage="Are you sure you want to delete this fun fact?"
                            successMessage="This fun fact has been successfully deleted."
                            modalFunction={handleFunFactDelete}
                            close={
                                <Button className="tertiary-btn" onClick={() => setShowDeleteFunFactModal(false)}>
                                    Back to Bot Settings
                                </Button>
                            }
                            error={error}
                        />
                        <ConfirmationModal
                            show={showDeleteHelpRequestEmailModal}
                            changeVisibility={setShowDeleteHelpRequestEmailModal}
                            type="delete"
                            confirmationButton="Delete Help Request Email"
                            confirmationMessage="Are you sure you want to delete this help request email?"
                            successMessage="This help request email has been successfully deleted."
                            modalFunction={handleDeleteHelpRequestEmail}
                            close={
                                <Button className="tertiary-btn" onClick={() => setShowDeleteHelpRequestEmailModal(false)}>
                                    Back to Bot Settings
                                </Button>
                            }
                            error={error}
                        />
                        <ConfirmationModal
                            show={ showEditSuccessModal }
                            changeVisibility={ setShowEditSuccessModal }
                            type="success"
                            successMessage="You have successfully updated the fun fact."
                            close={
                                <Button className="tertiary-btn" onClick={() => setShowEditSuccessModal(false)}>
                                    Back to Bot Settings
                                </Button>
                            } />
                    </>
            }
        </ContentWrapper>
    );
};

export default BotSettings;
